import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import SelectionCheckbox from '../../components/selection-checkbox';
import DropdownMenu from '../../components/dropdown-menu';
import { formatWeight } from './truck-assignments.utils';

/**
 * Ligne d'une commande : séquence, réceptionnaire + BL, ville, indicateurs,
 * poids, ETA et menu d'actions (SFD 5.1)
 */
export default function AssignmentRow({
  delivery,
  color,
  alerts,
  isSelected,
  isOpened,
  isLocked = false,
  onToggleSelect,
  onDragStart,
  onDragEnd,
  dropPosition,
  dropHandlers,
  onOpen,
  menuItems,
}) {
  // La ligne ne devient glissable que depuis la poignée : le reste de la
  // ligne permet de sélectionner du texte (n° de BL, adresse…)
  const [isDragArmed, setIsDragArmed] = useState(false);

  useEffect(() => {
    if (!isDragArmed) return undefined;
    const disarm = () => setIsDragArmed(false);
    document.addEventListener('mouseup', disarm);
    return () => document.removeEventListener('mouseup', disarm);
  }, [isDragArmed]);

  const isAssigned = Boolean(delivery.vehicleId);
  const isGeolocated = Boolean(delivery.coordinates);
  // La géolocalisation est déjà signalée dans le sous-titre
  const indicators = alerts.filter((alert) => alert.key !== 'geocoding');

  const blLabel =
    delivery.blKeys.length > 1
      ? `BL ${delivery.blKeys[0]} +${delivery.blKeys.length - 1}`
      : delivery.blKeys[0] && `BL ${delivery.blKeys[0]}`;

  return (
    <div
      className={`ta-row ${isSelected ? 'ta-row--selected' : ''} ${
        isOpened ? 'ta-row--opened' : ''
      } ${isLocked ? 'ta-row--locked' : ''} ${dropPosition ? `ta-row--drop-${dropPosition}` : ''}`}
      draggable={isDragArmed}
      {...dropHandlers}
      onDragStart={onDragStart}
      onDragEnd={(event) => {
        setIsDragArmed(false);
        onDragEnd(event);
      }}
    >
      <SelectionCheckbox
        checked={isSelected}
        onChange={onToggleSelect}
        title={
          isLocked
            ? 'Camion envoyé en exploitation : commande verrouillée'
            : 'Sélectionner la commande'
        }
        disabled={isLocked}
      />
      <span
        className="ta-row__handle"
        title={
          isLocked
            ? 'Camion envoyé en exploitation : commande verrouillée'
            : 'Glisser vers un camion'
        }
        onMouseDown={() => !isLocked && setIsDragArmed(true)}
      >
        ⋮⋮
      </span>
      <span
        className={`ta-row__seq ${isAssigned ? '' : 'ta-row__seq--empty'}`}
        style={isAssigned ? { backgroundColor: color } : undefined}
      >
        {isAssigned ? delivery.order || '' : ''}
      </span>
      <div className="ta-row__main">
        <div className="ta-row__line1">
          <span className="ta-row__client" title={delivery.address}>
            {delivery.client}
          </span>
          {blLabel && (
            <span
              className="ta-row__bl"
              title={delivery.blKeys.join(', ') || undefined}
            >
              {blLabel}
            </span>
          )}
          {!isAssigned && delivery.plannedVehicle && (
            <span
              className="ta-row__planned"
              title={`Camion prévu par le fichier : ${delivery.plannedVehicle}. La commande ne lui a pas été affectée d’office (camion indisponible, déjà envoyé ou inconnu).`}
            >
              Prévu : {delivery.plannedVehicle}
            </span>
          )}
        </div>
        <span
          className={`ta-row__sub ${isGeolocated ? '' : 'ta-row__sub--warn'}`}
        >
          {delivery.city}
          {' · '}
          {isGeolocated ? delivery.products : 'adresse non géolocalisée'}
        </span>
      </div>
      <div className="ta-row__indicators">
        {indicators.map((alert) => (
          <span
            key={alert.key}
            style={{ color: alert.color }}
            title={alert.title}
          >
            {alert.label}
          </span>
        ))}
      </div>
      <div className="ta-row__weight">{formatWeight(delivery.weight)}</div>
      <div
        className={`ta-row__eta ${
          delivery.etaExceedsTimeWindow ? 'ta-row__eta--late' : ''
        }`}
      >
        {isAssigned ? delivery.etaTime || '—' : ''}
        {isAssigned && delivery.etaDayOffset > 0 && (
          <span className="ta-row__eta-day">J+{delivery.etaDayOffset}</span>
        )}
      </div>
      <div className="ta-row__menu">
        <button
          type="button"
          className={`ta-icon-btn ${isOpened ? 'ta-icon-btn--active' : ''}`}
          onClick={onOpen}
          title="Voir le détail (BL, articles)"
        >
          <FontAwesomeIcon icon={faEye} />
        </button>
        <DropdownMenu
          items={menuItems}
          renderTrigger={({ toggle }) => (
            <button
              type="button"
              className="ta-icon-btn"
              onClick={toggle}
              title="Actions"
            >
              ···
            </button>
          )}
        />
      </div>
    </div>
  );
}
